import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { freezerModelPageRouteName } from "../list/index.page";
import {
  useFreezerModelServiceV1GetFreezerModelQuery,
  useFreezerModelServiceV1UpdateFreezerModelMutation,
} from "_services/modules/freezer_model";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface EditFreezerModelFormValues {
  freezerModelName: string;
  fuelEfficiency: string;
}

const useEditFreezerModelForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("freezerModel");

  const schema = useMemo(() => yup.object({
    freezerModelName: yup.string().required(),
    fuelEfficiency: yup
      .number()
      .transform((value, originalValue) => {
        if (typeof originalValue === "string" && originalValue !== "") {
          return Number(originalValue.replace(",", "."));
        }
        return value;
      })
      .typeError(t("error-type-number"))
      .required()
      .min(0),
  }).required(), [t]);

  const { data: detail, isLoading } = useFreezerModelServiceV1GetFreezerModelQuery(
    { id: id! },
    { skip: !id }
  );

  const [updateFreezerModel] = useFreezerModelServiceV1UpdateFreezerModelMutation();

  const freezerBrand = detail?.freezer_brand_name ?? "";

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    reset,
  } = useForm<EditFreezerModelFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      freezerModelName: "",
      fuelEfficiency: "",
    },
  });

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditFreezerModelFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (detail?.freezer_model_name && detail?.fuel_efficiency !== undefined) {
      reset({
        freezerModelName: detail.freezer_model_name,
        fuelEfficiency: String(detail.fuel_efficiency),
      });
    }
  }, [detail, reset]);

  const onSubmit = (data: EditFreezerModelFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      await updateFreezerModel({
        id,
        freezerModelServiceV1UpdateFreezerModelBody: {
          freezer_model_name: pendingData.freezerModelName,
          fuel_efficiency: Number(pendingData.fuelEfficiency),
        },
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      setShowSaveModal(false);
      if (error?.status === 409) {
        setError("freezerModelName", { type: "validate", message: t("error-duplicate-exists") });
      } else {
        errorHandler(error);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    setError,
    freezerBrand,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
    isLoading,
  };
};

export default useEditFreezerModelForm;
